import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import debounce from 'lodash/debounce';
import classNames from 'classnames';
import AutoComplete from './AutoComplete';
import TagInput from '../widgets/TagInput';
import css from './AutoCompleteField.css';
import QuestionMark from '../widgets/QuestionMark';
import { AutoCompleteOptionType } from './autocompleteTypes';
import AdvancedAutoComplete from './AdvancedAutoComplete/AdvancedAutoComplete';
import { AutoCompleteSizes } from './AdvancedAutoComplete/consts';

export const AUTOCOMPLETE_LAYOUTS = {
    HORIZONTAL: 'horizontal',
    VERTICAL: 'vertical',
};

function AutoCompleteField({
    label = '',
    labelTooltip = null,
    labelTooltipStyle = {},
    labelTranslateArgs = null,
    labelClassName,
    translate,
    options,
    value,
    isMulti = true,
    containerStyle = null,
    onChange,
    onInputChange = () => {},
    debounceTime = 200,
    disabled = false,
    placeholder = 'Select',
    error = '',
    big = false,
    selectOptions = {},
    searchable = false,
    onBlur = () => {},
    controlled = false,
    placeholderData = {},
    mandatory = false,
    clearable = false,
    copiable = false,
    loading = false,
    containerClass = {},
    innerContainerClass,
    errorClassName = '',
    openMenuOnClick = true,
    badgeText = null,
    badgeClass = null,
    dataTestId = null,
    advanced = false,
    hideDropdownIndicator = true,
    removable = false,
    onRemove,
    disableOptions = false,
    size,
    groupBy,
    defaultOpen = false,
    lazy = false,
    shouldWarnEmpty = false,
    creatable = false,
    hideDropdown = false,
    menuPlacement = 'auto',
    renderOption,
    layout = AUTOCOMPLETE_LAYOUTS.VERTICAL,
}) {
    const [inputValue, setInputValue] = useState(value || null);
    const handleInputChangeDebounce = useCallback(
        debounce(
            currentValue => {
                onInputChange(currentValue);
            },
            debounceTime,
            { trailing: true }
        ),
        []
    );
    const onInputChangeInner = currentValue => {
        setInputValue(currentValue);
        handleInputChangeDebounce(currentValue);
    };

    const onChangeInner = currentValue => {
        setInputValue(currentValue);
        onChange(currentValue);
    };

    useEffect(() => {
        if (controlled && value !== inputValue) {
            setInputValue(value);
        }
    }, [controlled, value]);

    if (advanced)
        return (
            <AdvancedAutoComplete
                options={options}
                values={value}
                label={label}
                onChange={onChange}
                className={innerContainerClass}
                disabled={disabled}
                loading={loading}
                removable={removable}
                onRemove={onRemove}
                groupBy={groupBy}
                size={size}
                defaultOpen={defaultOpen}
                lazy={lazy}
                isMulti={isMulti}
                shouldWarnEmpty={shouldWarnEmpty}
                renderOption={renderOption}
                clearable={clearable}
                disableOptions={disableOptions}
            />
        );

    const containerClasses = classNames(
        css.container,
        { [css.vertical]: layout === AUTOCOMPLETE_LAYOUTS.VERTICAL },
        containerClass
    );

    return (
        <div className={containerClasses} style={containerStyle}>
            <div className={classNames(css.labelText, labelClassName, { [css.error]: !!error })}>
                {mandatory ? <span className={css.mandatory}>*</span> : null}
                {badgeText ? (
                    <div className={classNames(css.badge, badgeClass)}>
                        <div className={css.badgeInner}>{badgeText}</div>
                    </div>
                ) : null}
                <span>{translate(label, labelTranslateArgs) || label}</span>
                {labelTooltip ? (
                    <QuestionMark message={labelTooltip} contentStyle={labelTooltipStyle} interactive />
                ) : null}
            </div>
            <div className={css.dropdownContainer}>
            {isMulti ? (
                <TagInput
                    creatable={creatable}
                    dataTestId={dataTestId}
                    ariaLabel={label}
                    placeholder={translate(placeholder, placeholderData) || placeholder}
                    suggestions={options}
                    tags={value || null}
                    disabled={disabled}
                    containerStyle={{
                        width: '100%',
                    }}
                    onChange={onChange}
                    error={error}
                    virtualScrolling
                    openMenuOnClick={openMenuOnClick}
                    className={innerContainerClass}
                    hideDropdownIndicator={hideDropdownIndicator}
                    disableOptions={disableOptions}
                    hideDropdown={hideDropdown}
                    menuPlacement={menuPlacement}
                />
            ) : (
                <AutoComplete
                    copiable={copiable}
                    ariaLabel={label}
                    dataTestId={dataTestId}
                    placeholder={translate(placeholder, placeholderData) || placeholder}
                    isCreatable={creatable}
                    selectOptions={{
                        options,
                        disabled,
                        ...(searchable
                            ? {
                                  noOptionsMessage: () => null,
                                  isSearchable: true,
                                  inputValue: typeof inputValue === 'string' ? inputValue : '',
                              }
                            : {}),
                        isClearable: clearable,
                        isLoading: loading,
                        menuPlacement,
                        ...selectOptions,
                    }}
                    defaultValue={inputValue}
                    onChange={onChangeInner}
                    onInputChange={onInputChangeInner}
                    onBlur={onBlur}
                    controlledValue={controlled ? inputValue : null}
                    searchable={searchable}
                    error={error}
                    big={big}
                    virtualScrolling
                    containerClass={innerContainerClass}
                />
            )}
            </div>
            {error && <div className={classNames(css.errorMessage, errorClassName)}>{translate(error)}</div>}
        </div>
    );
}

AutoCompleteField.propTypes = {
    label: PropTypes.string,
    labelTooltip: PropTypes.string,
    labelTooltipStyle: PropTypes.objectOf(PropTypes.any),
    labelTranslateArgs: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    labelClassName: PropTypes.string,
    options: PropTypes.arrayOf(AutoCompleteOptionType).isRequired,
    translate: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onInputChange: PropTypes.func,
    isMulti: PropTypes.bool,
    containerStyle: PropTypes.objectOf(PropTypes.any),
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
        PropTypes.arrayOf(PropTypes.any),
        PropTypes.string,
    ]),
    error: PropTypes.string,
    big: PropTypes.bool,
    selectOptions: PropTypes.objectOf(PropTypes.any),
    searchable: PropTypes.bool,
    clearable: PropTypes.bool,
    copiable: PropTypes.bool,
    loading: PropTypes.bool,
    onBlur: PropTypes.func,
    controlled: PropTypes.bool,
    placeholderData: PropTypes.objectOf(PropTypes.any),
    mandatory: PropTypes.bool,
    debounceTime: PropTypes.number,
    containerClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    innerContainerClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    errorClassName: PropTypes.string,
    openMenuOnClick: PropTypes.bool,
    badgeText: PropTypes.string,
    badgeClass: PropTypes.string,
    dataTestId: PropTypes.string,
    advanced: PropTypes.bool,
    hideDropdownIndicator: PropTypes.bool,
    removable: PropTypes.bool,
    onRemove: PropTypes.func,
    disableOptions: PropTypes.bool,
    groupBy: PropTypes.func,
    size: PropTypes.oneOf(Object.values(AutoCompleteSizes)),
    defaultOpen: PropTypes.bool,
    lazy: PropTypes.bool,
    shouldWarnEmpty: PropTypes.bool,
    creatable: PropTypes.bool,
    hideDropdown: PropTypes.bool,
    menuPlacement: PropTypes.oneOf(['auto', 'top', 'bottom']),
    renderOption: PropTypes.func,
    layout: PropTypes.oneOf(Object.values(AUTOCOMPLETE_LAYOUTS)),
};

export default withLocalize(AutoCompleteField);
